import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Facebook, MessageCircle, Download } from "lucide-react";
import { ASSETS, SCHOOL_INFO } from "@/lib/assets";
import { triggerInstall } from "@/lib/installPrompt";
import { toast } from "sonner";

const handleInstallSite = async () => {
  const outcome = await triggerInstall();
  if (outcome === "accepted") {
    toast.success("Installation lancée ! L'icône Mazid Connect va apparaître sur votre appareil.");
  } else if (outcome === "unavailable") {
    toast.info(
      "Pour installer : Chrome/Edge → icône d'installation dans la barre d'adresse (ou Menu → Installer l'application). iPhone/Safari → Partager → « Sur l'écran d'accueil ».",
      { duration: 9000 }
    );
  }
};

export default function Footer() {
  return (
    <footer className="bg-[#1E3A8A] text-white" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={ASSETS.logo} alt="Logo Les Écoles Mazid" className="h-14 w-14 rounded-xl object-contain bg-white p-0.5" />
            <div>
              <div className="font-heading font-extrabold text-lg leading-none">LES ÉCOLES MAZID</div>
              <div className="text-[10px] tracking-[0.25em] text-[#FBBF24] uppercase font-semibold mt-1">
                {SCHOOL_INFO.tagline}
              </div>
            </div>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">
            Former, accompagner et révéler le potentiel de chaque enfant. Une école engagée pour l&apos;excellence pédagogique en Guinée.
          </p>
        </div>

        <div>
          <h4 className="font-heading font-bold mb-4 text-[#FBBF24] uppercase text-xs tracking-[0.2em]">Navigation</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-[#FBBF24] transition-colors">Accueil</Link></li>
            <li><Link to="/a-propos" className="hover:text-[#FBBF24] transition-colors">À propos</Link></li>
            <li><Link to="/cycles" className="hover:text-[#FBBF24] transition-colors">Nos cycles</Link></li>
            <li><Link to="/admissions" className="hover:text-[#FBBF24] transition-colors">Admissions</Link></li>
            <li><Link to="/actualites" className="hover:text-[#FBBF24] transition-colors">Actualités</Link></li>
            <li><Link to="/agrements" className="hover:text-[#FBBF24] transition-colors" data-testid="footer-agrements-link">Agréments officiels</Link></li>
            <li><Link to="/demo" className="hover:text-[#FBBF24] transition-colors" data-testid="footer-demo-link">Visite guidée</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold mb-4 text-[#FBBF24] uppercase text-xs tracking-[0.2em]">Espaces</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/connexion" className="hover:text-[#FBBF24] transition-colors">Espace Parents</Link></li>
            <li><Link to="/connexion" className="hover:text-[#FBBF24] transition-colors">Espace Enseignants</Link></li>
            <li><Link to="/connexion" className="hover:text-[#FBBF24] transition-colors">Espace Administration</Link></li>
            <li><Link to="/inscription" className="hover:text-[#FBBF24] transition-colors">Inscrire un enfant</Link></li>
            <li>
              <button
                onClick={handleInstallSite}
                className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-[#FBBF24] text-[#1E3A8A] font-bold text-xs hover:bg-amber-300 transition-colors"
                data-testid="footer-install-app-btn"
              >
                <Download className="h-4 w-4" />
                Installer l&apos;application
              </button>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold mb-4 text-[#FBBF24] uppercase text-xs tracking-[0.2em]">Contact</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-[#FBBF24] flex-shrink-0" />
              <span className="text-slate-300">{SCHOOL_INFO.address}</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-[#FBBF24]" />
              <span className="text-slate-300">{SCHOOL_INFO.phones.join(" · ")}</span>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#FBBF24]" />
              <span className="text-slate-300">{SCHOOL_INFO.email}</span>
            </li>
            <li className="flex items-center gap-3 pt-2">
              <a href={SCHOOL_INFO.facebook} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-[#FBBF24] hover:text-[#1E3A8A] transition" data-testid="footer-facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={`https://wa.me/${SCHOOL_INFO.whatsapp.replace(/\s|\+/g, "")}`} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-[#FBBF24] hover:text-[#1E3A8A] transition" data-testid="footer-whatsapp">
                <MessageCircle className="h-4 w-4" />
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-400 space-y-1">
        <p data-testid="footer-arrete">{SCHOOL_INFO.arrete}</p>
        <p>© {new Date().getFullYear()} Les Écoles Mazid — Mazid School Connect. Tous droits réservés.</p>
      </div>
    </footer>
  );
}
